import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { useFieldArray, Control } from 'react-hook-form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationFieldRow } from '@/features/scheduling/components/NotificationFieldRow';
import { SchedulingFormData } from '@/features/scheduling/types/SchedulingFormType.interface';

type EverydaySchedulingFormProps = {
  control: Control<SchedulingFormData>;
};

export const EverydaySchedulingForm = ({
  control,
}: EverydaySchedulingFormProps) => {
  const { t } = useTranslation();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'schedulings',
  });

  const getNotificationCountForEveryDay = (): number => {
    return fields.filter((n) => n?.day === 'EVERY_DAY').length || 0;
  };

  const [notificationCount, setNotificationCount] = useState<number>(() => {
    return getNotificationCountForEveryDay();
  });

  const updateNotificationFields = (count: number) => {
    const existingFields = fields.filter((field) => field.day === 'EVERY_DAY');
    const currentCount = existingFields.length;

    if (count > currentCount) {
      for (let i = currentCount; i < count; i++) {
        append({
          day: 'EVERY_DAY',
          startTime: null,
          endTime: null,
        });
      }
    } else if (count < currentCount) {
      const fieldsToRemove = existingFields.slice(count);
      fieldsToRemove.forEach((field) => {
        const index = fields.findIndex((f) => f.id === field.id);
        if (index !== -1) {
          remove(index);
        }
      });
    }

    setNotificationCount(count);
  };

  const addNotification = () => {
    const newCount = notificationCount + 1;
    updateNotificationFields(newCount);
  };

  const everydayFields = fields
    .map((field, index) => ({ field, index }))
    .filter(({ field }) => field.day === 'EVERY_DAY');

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 1,
        mb: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {t('pages.studies.emaQuestionnaire.scheduling.notifications')}
        </Typography>
        <TextField
          type="number"
          size="small"
          slotProps={{
            htmlInput: {
              min: 1,
              max: 50,
            },
          }}
          value={notificationCount}
          onChange={(e) => {
            const newValue = Math.min(50, parseInt(e.target.value) || 0);

            const value = Math.max(0, newValue);
            updateNotificationFields(value);
          }}
          sx={{ width: 100 }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={addNotification}
        >
          {t('buttons.addNotification')}
        </Button>
      </Box>

      {everydayFields.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('pages.studies.emaQuestionnaire.scheduling.notifications')}
          </Typography>
          {everydayFields.map(({ field, index }, indexOfDay) => (
            <NotificationFieldRow
              key={field.id}
              control={control}
              index={index}
              indexOfDay={indexOfDay}
              dayDisplayName={t(
                'pages.studies.emaQuestionnaire.scheduling.daily',
              )}
              initialShowEndTime={field.startTime?.diff(field.endTime) !== 0}
              onRemove={() => {
                remove(index);
                setNotificationCount((prev) => prev - 1);
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
